/**
 * QuestionStep Component
 * Extracted from DeviceSpecifications.tsx
 * Handles dynamic Supabase-driven question flow UI and logic
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  // UI / status (also used directly in JSX)
  CheckCircle,
  Lightbulb,
  Check,
  X,
  HelpCircle,
  // Authenticity / repair
  ShieldCheck,
  Wrench,
  // Condition gradient (best -> worst)
  Sparkles,
  Gem,
  Star,
  ThumbsUp,
  AlertCircle,
  AlertTriangle,
  XCircle,
  // Battery
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  BatteryWarning,
  BatteryCharging,
  // Connectivity / storage
  Wifi,
  Signal,
  RadioTower,
  HardDrive,
  MemoryStick,
  Lock,
  LockOpen,
  // Camera
  Camera,
  CameraOff,
  Aperture,
  // Accessories
  Package,
  PackageCheck,
  PackageX,
  Plug,
  Cable,
  Watch,
  // Device / components
  Smartphone,
  Tablet,
  ScanFace,
  Fingerprint,
  Volume2,
  Mic,
  type LucideIcon,
} from 'lucide-react';
// Device-specific Tabler icons for a cohesive, premium condition/device set.
import {
  IconDeviceMobileCheck,
  IconDeviceMobileX,
  IconDeviceMobile,
  IconDeviceMobileExclamation,
  IconDeviceMobileQuestion,
  IconDeviceMobileCharging,
  IconCameraCheck,
  IconCameraX,
  IconCameraExclamation,
} from '@tabler/icons-react';
import styled, { keyframes, css } from 'styled-components';
import type { DynamicQuestion, DynamicQuestionOption } from './types';

// Returns the icon for an answer option, using BOTH the option key and the
// QUESTION key as context (most answer keys are not self-descriptive, e.g.
// 'boven_85', 'krasvrij', 'compleet', 'beide'). Single-colour line icons — the
// icon SHAPE carries the meaning, so each answer in a question gets a clearly
// distinct silhouette. No colour coding.
const getOptionIcon = (optionKey: string, questionKey: string = ''): LucideIcon => {
  const k = optionKey.toLowerCase();
  const q = questionKey.toLowerCase();

  // === VERSION (Wi-Fi / Cellular storage variants) ===
  if (q.includes('versie') || q.includes('version')) {
    if (k.includes('cellular')) return Signal;
    return Wifi;
  }

  // === BATTERY CONDITION (honderd / boven_85 / onder_85 / onbekend) ===
  // Battery fill level itself communicates the answer; unknown gets a question mark.
  if (q.includes('batterij') || q.includes('battery') || q.includes('accu')) {
    if (k.includes('onbekend') || k.includes('unknown')) return HelpCircle;
    if (k.includes('honderd') || k.includes('100')) return BatteryFull;
    if (k.includes('boven') || k.includes('above')) return BatteryMedium;
    if (k.includes('onder') || k.includes('below') || k.includes('service')) return BatteryLow;
    return BatteryMedium;
  }

  // === CHARGING (opladen: ja / niet) ===
  if (q.includes('opladen') || q.includes('oplaad') || q.includes('charg')) {
    if (k.includes('niet') || k.includes('nee') || k === 'no' || k.includes('not')) return XCircle;
    return BatteryCharging;
  }

  // === ACCESSORIES (compleet / met_lader / met_kabel / met_bandje / alleen) ===
  if (q.includes('toebehoren') || q.includes('accessoir') || q.includes('accessor') || q.includes('extras')) {
    if (k.includes('compleet') || k.includes('complete') || k.includes('alles') || k.includes('all')) return PackageCheck;
    if (k.includes('lader') || k.includes('charger') || k.includes('adapter')) return Plug;
    if (k.includes('kabel') || k.includes('cable')) return Cable;
    if (k.includes('bandje') || k.includes('band') || k.includes('strap')) return Watch;
    if (k.includes('alleen') || k.includes('only') || k.includes('geen') || k.includes('none')) return PackageX;
    return Package;
  }

  // === CAMERA (perfect = OK; defect / lens / beide = damaged) ===
  if (q.includes('camera') || q.includes('foto')) {
    if (k.includes('perfect') || k === 'ja' || k.includes('werkt') || k.includes('working') || k.includes('ok')) return Camera;
    return CameraOff;
  }

  // === SCREEN / HOUSING CONDITION (krasvrij -> licht -> diep/duidelijk -> defect/gebarsten) ===
  // Consistent status family — the symbol inside reads instantly and matches the
  // label: check (flawless) -> info (light traces) -> warning (clear damage) ->
  // X (broken). Battery already handled above, so 'conditie' here = scherm_conditie.
  if (q.includes('scherm') || q.includes('behuizing') || q.includes('conditie') || q.includes('screen') || q.includes('housing') || q.includes('body')) {
    if (k.includes('krasvrij') || k.includes('flawless') || k.includes('perfect') || k.includes('mint')) return CheckCircle;
    if (k.includes('gebarsten') || k.includes('barst') || k.includes('gebroken') || k.includes('crack') || k.includes('broken') || k.includes('defect') || k.includes('gebogen')) return XCircle;
    if (k.includes('diep') || k.includes('deep') || k.includes('duidelijke') || k.includes('heavy') || k.includes('zwaar')) return AlertTriangle;
    if (k.includes('licht') || k.includes('light') || k.includes('sporen') || k.includes('minor')) return AlertCircle;
    return AlertCircle;
  }

  // === GENERAL STATE (algemene_staat: labels are literally Ja / Nee / Ik weet het niet) ===
  // Match the LABEL: check (Ja/origineel) / X (Nee/vervangen) / question mark (unknown).
  if (q.includes('algemene') || q.includes('staat_van') || q === 'staat') {
    if (k.includes('onbekend') || k.includes('unknown')) return HelpCircle;
    if (k.includes('vervangen') || k.includes('replaced') || k === 'nee' || k === 'no') return XCircle;
    if (k.includes('origineel') || k.includes('original') || k.includes('authentic') || k === 'ja' || k === 'yes') return CheckCircle;
    return CheckCircle;
  }

  // --- Generic binary / status ---
  if (k === 'yes' || k === 'ja' || k.includes('working') || k.includes('werkt')) return Check;
  if (k === 'no' || k === 'nee' || k.includes('niet') || k.includes('defect') || k.includes('kapot') || k.includes('broken')) return XCircle;
  if (k.includes('unknown') || k.includes('onbekend')) return HelpCircle;

  // --- Storage / memory ---
  if (k.includes('gb') || k.includes('tb') || k.includes('storage') || k.includes('opslag') || k.includes('capacit')) return HardDrive;
  if (k.includes('ram') || k.includes('geheugen') || k.includes('memory')) return MemoryStick;

  // --- Connectivity ---
  if (k.includes('simlock') || k.includes('unlocked') || k.includes('simvrij')) return LockOpen;
  if (k.includes('locked') || k.includes('provider')) return Lock;
  if (k.includes('cellular') || k.includes('4g') || k.includes('5g')) return Signal;
  if (k.includes('wifi') || k.includes('connectivity')) return Wifi;

  // --- Cosmetic condition (no question context matched) ---
  if (k.includes('krasvrij') || k.includes('flawless')) return Sparkles;
  if (k.includes('nieuw') || k.includes('new') || k.includes('sealed') || k.includes('mint')) return Gem;
  if (k.includes('perfect') || k.includes('zgan')) return Sparkles;
  if (k.includes('gebarsten') || k.includes('barst') || k.includes('crack') || k.includes('gebroken')) return XCircle;
  if (k.includes('diep') || k.includes('deep') || k.includes('heavy') || k.includes('zwaar') || k.includes('duidelijke')) return AlertTriangle;
  if (k.includes('licht') || k.includes('light') || k.includes('sporen') || k.includes('minor') || k.includes('goed') || k.includes('good')) return ThumbsUp;
  if (k.includes('schade') || k.includes('damage')) return AlertTriangle;

  // --- Components ---
  if (k.includes('scherm') || k.includes('screen') || k.includes('display') || k.includes('lcd') || k.includes('glas')) return Smartphone;
  if (k.includes('behuizing') || k.includes('housing') || k.includes('frame') || k.includes('achterkant')) return Tablet;
  if (k.includes('face') || k.includes('gezicht')) return ScanFace;
  if (k.includes('touch') || k.includes('finger') || k.includes('vinger')) return Fingerprint;
  if (k.includes('camera') || k.includes('foto') || k.includes('lens')) return Camera;
  if (k.includes('geluid') || k.includes('sound') || k.includes('speaker') || k.includes('luidspreker')) return Volume2;
  if (k.includes('mic') || k.includes('microfoon')) return Mic;
  if (k.includes('repair') || k.includes('reparatie') || k.includes('vervangen')) return Wrench;

  // Generic fallback
  if (k.includes('staat') || k.includes('state') || k.includes('condition')) return Star;
  return CheckCircle;
};

// Icon registry: maps an icon_name string (stored in Supabase per answer option,
// editable in the admin panel) to a concrete icon component. This is the
// data-driven source of truth; getOptionIcon below is only the fallback for
// answers whose icon_name is still empty.
const ICON_REGISTRY: Record<string, React.ComponentType<any>> = {
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'help-circle': HelpCircle,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'shield-check': ShieldCheck,
  'sparkles': Sparkles,
  'wrench': Wrench,
  'star': Star,
  'gem': Gem,
  'thumbs-up': ThumbsUp,
  'battery-full': BatteryFull,
  'battery-medium': BatteryMedium,
  'battery-low': BatteryLow,
  'battery-charging': BatteryCharging,
  'battery-warning': BatteryWarning,
  'camera': Camera,
  'camera-off': CameraOff,
  'aperture': Aperture,
  'wifi': Wifi,
  'signal': Signal,
  'hard-drive': HardDrive,
  'memory-stick': MemoryStick,
  'package-check': PackageCheck,
  'package-x': PackageX,
  'package': Package,
  'plug': Plug,
  'cable': Cable,
  'watch': Watch,
  'smartphone': Smartphone,
  'tablet': Tablet,
  'lock': Lock,
  'lock-open': LockOpen,
  'radio-tower': RadioTower,
  // Tabler device-specific (premium, cohesive condition/device family)
  'device-mobile-check': IconDeviceMobileCheck,
  'device-mobile-x': IconDeviceMobileX,
  'device-mobile': IconDeviceMobile,
  'device-mobile-exclamation': IconDeviceMobileExclamation,
  'device-mobile-question': IconDeviceMobileQuestion,
  'device-mobile-charging': IconDeviceMobileCharging,
  'camera-check': IconCameraCheck,
  'camera-x': IconCameraX,
  'camera-exclamation': IconCameraExclamation,
};

// Resolve the icon for an answer option: prefer the Supabase-managed icon_name
// (so icons are data-driven and editable in the admin panel), and fall back to
// the key-based heuristic only when icon_name is empty or unknown.
const resolveOptionIcon = (
  iconName: string | null | undefined,
  optionKey: string,
  questionKey: string
): React.ComponentType<any> => {
  if (iconName && ICON_REGISTRY[iconName]) return ICON_REGISTRY[iconName];
  return getOptionIcon(optionKey, questionKey);
};

// Optional idle animation hint for power-related answers (a gentle pulse that
// reinforces "energy"). Kept to single-occurrence icons so a screen never has
// more than one pulsing icon — the version grid (many wifi/signal) gets none.
const getIconFx = (optionKey: string, questionKey: string = ''): '' | 'charge' => {
  const k = optionKey.toLowerCase();
  const q = questionKey.toLowerCase();
  if ((q.includes('opladen') || q.includes('oplaad') || q.includes('charg')) &&
      !(k.includes('niet') || k.includes('nee') || k === 'no' || k.includes('not'))) {
    return 'charge'; // "laadt goed op"
  }
  if ((q.includes('toebehoren') || q.includes('accessoir') || q.includes('accessor')) &&
      (k.includes('lader') || k.includes('charger') || k.includes('adapter'))) {
    return 'charge'; // "met lader"
  }
  return '';
};


// Styled Components
const phoneSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-80px);
  }
  to {
    opacity: 1;
    transform: translateX(-40px);
  }
`;

const phoneSlideInWide = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(-64px);
  }
`;

const QuestionContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
}) <{ visible: boolean }>`
  display: ${props => props.visible ? 'block' : 'none'};
`;

const QuestionTwoCol = styled.div`
  display: grid;
  grid-template-columns: 160px 1px 1fr;
  gap: 24px;
  align-items: start;
  min-width: 100%;
  width: 100%;
  
  > div:last-child {
    min-width: 600px;
    width: 100%;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1px 1fr;
    > div:last-child {
      min-width: 500px;
    }
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    > div:last-child {
      min-width: 100%;
    }
  }
`;

const HelperRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 24px;
  align-items: start;
  margin-top: 0;
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1fr;
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const IllustrationPanel = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
  }
  margin: 0;
`;

const ColumnDivider = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
    align-self: stretch;
    justify-self: center;
    width: 1px;
    background: linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.04) 100%);
    border-radius: 1px;
    transform: translateX(-16px);
    @media (min-width: 1280px) {
      transform: translateX(-22px);
    }
  }
`;

const IllustrationCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  height: 380px;
  position: relative;
  overflow: hidden;
  display: block;
  margin-left: -1.25rem;
  box-shadow: none;
`;

const PhoneIllustrationFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;

  img, .device-image {
    height: 100%;
    width: auto;
    max-width: none;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 12px 20px rgba(0,0,0,0.12));
    transform: translateX(-40px);
    will-change: transform, opacity;
    animation: ${phoneSlideIn} 1200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms both;
  }

  @media (min-width: 1280px) {
    img, .device-image {
      height: 100%;
      transform: translateX(-64px);
      animation: ${phoneSlideInWide} 1400ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both;
    }
  }
`;

// Subtle icon animations — premium feel without being busy.
const iconCharge = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(1.12); }
`;
const iconPopIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.18); }
  100% { transform: scale(1); opacity: 1; }
`;

const OptionsGrid = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'columns' && prop !== 'keepColumnsOnMobile',
}) <{ columns?: number; keepColumnsOnMobile?: boolean }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 3}, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 0.5rem;
  justify-content: stretch;
  width: 100%;

  ${props => props.columns === 2 && `
    max-width: 600px;
    margin-left: 0;
    margin-right: auto;
  `}
  ${props => props.columns === 3 && `
    max-width: 900px;
    margin-left: 0;
    margin-right: auto;
  `}

  @media (max-width: 1024px) {
    grid-template-columns: repeat(${props => Math.min(2, (props.columns || 3))}, minmax(180px, 1fr));
    ${props => props.columns === 2 && `
      max-width: 500px;
    `}
    ${props => props.columns === 3 && `
      max-width: 100%;
    `}
  }
  @media (max-width: 640px) {
    /* Default: collapse naar 1 kolom. Voor de versie-vraag behouden we 2 kolommen
       (Wi-Fi links, Wi-Fi + Cellular rechts) — minmax(0, 1fr) zorgt dat smalle
       schermen niet over-scrollen. */
    grid-template-columns: ${props => props.keepColumnsOnMobile
      ? `repeat(${props.columns || 2}, minmax(0, 1fr))`
      : '1fr'};
    gap: 0.625rem;
    max-width: 100%;
    margin-bottom: 0.25rem;
  }
`;

const OptionCard = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'variant' && prop !== 'compact',
}) <{ selected?: boolean; variant?: string; compact?: boolean }>`
  background: #ffffff;
  color: #1e293b;
  border: 1.5px solid #e2e8f0;
  padding: ${props => props.compact ? '0.9rem 1rem' : '1rem 1.25rem'};
  border-radius: 16px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${props => props.compact ? '0.75rem' : '1rem'};
  min-height: ${props => props.compact ? '64px' : '80px'};
  position: relative;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    border-color: #94a3b8;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  ${props => props.selected && `
    border: 2px solid #1e293b;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.10);
    padding: ${props.compact ? 'calc(0.9rem - 0.5px) calc(1rem - 0.5px)' : 'calc(1rem - 0.5px) calc(1.25rem - 0.5px)'};
  `}

  @media (max-width: 640px) {
    /* Compact (versie-vraag): verticaal gestapeld op mobile zodat 2 kolommen
       netjes passen. Andere vragen blijven horizontaal. */
    flex-direction: ${props => props.compact ? 'column' : 'row'};
    text-align: ${props => props.compact ? 'center' : 'left'};
    justify-content: ${props => props.compact ? 'center' : 'flex-start'};
    padding: ${props => props.compact ? '0.75rem 0.5rem' : '1rem 1.125rem'};
    gap: ${props => props.compact ? '0.4rem' : '0.75rem'};
    min-height: 0;
    border-radius: 12px;

    ${props => props.selected && `
      padding: ${props.compact ? 'calc(0.75rem - 0.5px) calc(0.5rem - 0.5px)' : 'calc(1rem - 0.5px) calc(1.125rem - 0.5px)'};
    `}
  }

  .dark & {
    background: #0f1216;
    border-color: #262b36;
    color: #f1f5f9;

    &:hover {
      background: #151820;
      border-color: #3a4250;
      box-shadow: 0 8px 24px rgba(0,0,0,0.4);
    }
  }

  ${props => props.selected && `
    .dark & {
      background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%);
      border: 2px solid #e2e8f0;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }
  `}
`;

const OptionIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'compact' && prop !== 'fx',
}) <{ selected?: boolean; compact?: boolean; fx?: '' | 'charge' }>`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${props => props.selected ? '#1e293b' : '#f1f5f9'};
  color: ${props => props.selected ? '#ffffff' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  /* Springy scale on card hover */
  ${OptionCard}:hover & svg {
    transform: scale(1.15);
  }

  /* Gentle idle pulse for power-related answers (only when not selected) */
  ${props => props.fx === 'charge' && !props.selected && css`
    svg { animation: ${iconCharge} 1.9s ease-in-out infinite; }
  `}

  /* One-shot pop when an answer becomes selected */
  ${props => props.selected && css`
    svg { animation: ${iconPopIn} 0.34s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  `}

  @media (max-width: 640px) {
    width: ${props => props.compact ? '32px' : '36px'};
    height: ${props => props.compact ? '32px' : '36px'};
    border-radius: ${props => props.compact ? '10px' : '10px'};
    svg { width: ${props => props.compact ? '16px' : '18px'}; height: ${props => props.compact ? '16px' : '18px'}; }
  }

  .dark & {
    background: ${props => props.selected ? '#e2e8f0' : '#1e293b'};
    color: ${props => props.selected ? '#0f172a' : '#94a3b8'};
  }
`;

const SelectMarker = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'hideOnMobile',
}) <{ hideOnMobile?: boolean }>`
  margin-left: auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1e293b;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .dark & {
    background: #e2e8f0;
    color: #0f172a;
  }

  @media (max-width: 640px) {
    ${props => props.hideOnMobile && `display: none;`}
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;


const OptionText = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  letter-spacing: -0.01em;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }

  .dark & {
    color: #f1f5f9;
  }
`;

const OptionDescription = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
  margin-top: -0.1rem;
  max-width: 100%;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }

  .dark & {
    color: #94a3b8;
  }
`;

const HelperText = styled.div`
  margin-top: 2rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
  border-radius: 14px;
  border: 1px solid #e0ecf4;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  transition: all 0.2s ease;

  .helper-icon {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    background: #1e293b;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
  }

  .helper-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  h4 {
    font-size: 0.82rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.3;
  }

  p {
    font-size: 0.8rem;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
  }

  @media (max-width: 640px) {
    display: none;
  }

  .dark & {
    background: linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.4) 100%);
    border-color: #262b36;

    .helper-icon {
      background: #e2e8f0;
      color: #0f172a;
    }

    h4 { color: #f1f5f9; }
    p { color: #94a3b8; }
  }
`;




// Enhanced device image component with fallback system
const DeviceImageWithFallback: React.FC<{
  modelName: string;
  alt: string;
  className?: string;
  imageSrc?: string; // Database image URL (takes precedence over generated paths)
}> = ({ modelName, alt, className, imageSrc }) => {
  const [currentImagePath, setCurrentImagePath] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Get multiple image variants in quality priority order (highest quality first)
  const getImageVariants = (modelName: string): string[] => {
    const name = modelName.toLowerCase().replace(/\s+/g, '-');

    const baseVariants: string[] = [];

    if (name.includes('iphone')) {
      const iphoneModel = name.replace('iphone-', '').replace(/[^a-z0-9-]/g, '-');
      const pascalModel = iphoneModel.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('-');
      baseVariants.push(
        `/devices/iphone-${iphoneModel}.png`,
        `/devices/Apple-Iphone-${pascalModel}-PNG.png`,
        `/devices/iphone-${iphoneModel}-hd.png`,
        `/devices/iphone-${iphoneModel}-high.png`,
        `/devices/iphone-${iphoneModel}-medium-png.png`,
      );
      const genMatch = iphoneModel.match(/^(\d+)/);
      if (genMatch) {
        const prevGen = parseInt(genMatch[1]) - 1;
        if (prevGen >= 7) {
          baseVariants.push(
            `/devices/iphone-${prevGen}-pro-max.png`,
            `/devices/iphone-${prevGen}-pro.png`,
            `/devices/iphone-${prevGen}.png`,
          );
        }
      }
      baseVariants.push(
        `/devices/iphone-medium-png.png`,
        `/devices/iphone.png`
      );
    } else if (name.includes('ipad')) {
      const ipadModel = name.replace('ipad-', '').replace(/[^a-z0-9-]/g, '-');

      if (ipadModel.includes('pro-13-7') || ipadModel.includes('pro-13')) {
        baseVariants.push(
          `/iPad/ipad-pro-13inch-7de-gen-medium-png.png`,
          `/devices/ipad-pro-13-7.png`
        );
      } else if (ipadModel.includes('pro-12-9-6') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('6'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-6de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-6.png`
        );
      } else if (ipadModel.includes('pro-12-9-5') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('5'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-5de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-5.png`
        );
      } else if (ipadModel.includes('pro-12-9-4') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('4'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-4de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-4.png`
        );
      } else {
        baseVariants.push(
          `/devices/ipad-${ipadModel}.png`,
          `/devices/ipad-${ipadModel}-hd.png`,
          `/devices/ipad.png`
        );
      }
    } else if (name.includes('macbook')) {
      const macbookModel = name.replace('macbook-', '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/devices/macbook-${macbookModel}.png`,
        `/devices/macbook-${macbookModel}-hd.png`,
        `/devices/macbook.png`
      );
    } else if (name.includes('samsung') || name.includes('galaxy')) {
      const samsungModel = name.replace(/^(samsung-|galaxy-)/, '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/devices/galaxy-${samsungModel}.png`,
        `/devices/galaxy-${samsungModel}-hd.png`,
        `/devices/galaxy.png`
      );
    } else {
      baseVariants.push(`/devices/${name}.png`, `/devices/device.png`);
    }

    return baseVariants;
  };

  const loadIdRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!modelName) {
      setIsLoading(false);
      return;
    }

    const thisLoadId = ++loadIdRef.current;

    // Build variants array - prioritize imageSrc from database if provided
    const fallbackVariants = getImageVariants(modelName);
    const variants = imageSrc && imageSrc !== '/devices/default.png'
      ? [imageSrc, ...fallbackVariants]
      : fallbackVariants;

    let variantIndex = 0;
    let cancelled = false;

    const tryNextVariant = () => {
      if (cancelled || loadIdRef.current !== thisLoadId) return;
      if (variantIndex >= variants.length) {
        if (!currentImagePath) setImageError(true);
        setIsLoading(false);
        return;
      }

      const img = new Image();
      const currentVariant = variants[variantIndex];
      // Don't set currentImagePath here - wait for onload to confirm it works

      img.onload = () => {
        if (cancelled || loadIdRef.current !== thisLoadId) return;
        setCurrentImagePath(currentVariant); // Only set after successful load
        setImageError(false);
        setIsLoading(false);
      };

      img.onerror = () => {
        if (cancelled || loadIdRef.current !== thisLoadId) return;
        variantIndex++;
        tryNextVariant();
      };

      img.src = encodeURI(currentVariant);
    };

    // Only show loading spinner on first load
    if (!currentImagePath) {
      setIsLoading(true);
    }
    setImageError(false);
    tryNextVariant();

    return () => { cancelled = true; };
  }, [modelName, imageSrc]);

  if (!modelName) return null;

  if (imageError || !currentImagePath) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '0.875rem' }}>
        {alt}
      </div>
    );
  }

  return (
    <img
      src={encodeURI(currentImagePath)}
      alt={alt}
      className={className}
      style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
      loading="lazy"
    />
  );
};

// Component Props
interface QuestionStepProps {
  questions: DynamicQuestion[];
  currentQuestionIndex: number;
  answers: Record<string, string>;
  onAnswer: (questionKey: string, optionKey: string) => void;
  onNext: () => void;
  onPrev: () => void;
  isFirst: boolean;
  isLast: boolean;
  modelName: string;
  imageSrc?: string; // Database image URL for device illustration
  t: (key: string) => string;
  visible: boolean;
}

// Main Component
const QuestionStep: React.FC<QuestionStepProps> = ({
  questions,
  currentQuestionIndex,
  answers,
  onAnswer,
  onNext,
  onPrev,
  isFirst,
  isLast,
  modelName,
  imageSrc,
  t,
  visible,
}) => {
  const lastClickRef = React.useRef(0);

  // Get current question
  const currentQuestion = currentQuestionIndex >= 0 && currentQuestionIndex < questions.length
    ? questions[currentQuestionIndex]
    : null;

  if (!visible) {
    return null;
  }

  const selectedOptionKey = currentQuestion ? answers[currentQuestion.questionKey] || '' : '';

  return (
    <QuestionContainer visible={visible}>
      <AnimatePresence mode="popLayout">
        {currentQuestion && (
          <motion.div
            key={`question-${currentQuestionIndex}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <QuestionTwoCol>
              <IllustrationPanel>
                <IllustrationCard>
                  <PhoneIllustrationFrame>
                    <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                  </PhoneIllustrationFrame>
                </IllustrationCard>
              </IllustrationPanel>
              <ColumnDivider />
              <div>
                <OptionsGrid
                  columns={
                    currentQuestion.questionKey === 'versie'
                      ? 2  /* Wi-Fi links, Cellular rechts — gepaired per opslag/maat */
                      : (currentQuestion.options.length > 4 ? 3 : 2)
                  }
                  keepColumnsOnMobile={currentQuestion.questionKey === 'versie'}
                >
                  {currentQuestion.options.map((option: DynamicQuestionOption) => {
                    const isSelected = selectedOptionKey === option.optionKey;
                    const isVersieOption = currentQuestion.questionKey === 'versie';
                    return (
                      <OptionCard
                        key={option.id}
                        selected={isSelected}
                        compact={isVersieOption}
                        onClick={() => {
                          const now = Date.now();
                          if (now - lastClickRef.current < 180) return;
                          lastClickRef.current = now;
                          onAnswer(currentQuestion.questionKey, option.optionKey);
                          if (!isLast) {
                            onNext();
                          }
                        }}
                      >
                        <OptionIcon selected={isSelected} compact={isVersieOption} fx={getIconFx(option.optionKey, currentQuestion.questionKey)}>
                          {(() => {
                            const Icon = resolveOptionIcon(option.iconName, option.optionKey, currentQuestion.questionKey);
                            return <Icon />;
                          })()}
                        </OptionIcon>
                        <ContentColumn>
                          <OptionText>{option.label}</OptionText>
                          {option.description && (
                            <OptionDescription>{option.description}</OptionDescription>
                          )}
                        </ContentColumn>
                        {isSelected && (
                          <SelectMarker hideOnMobile={isVersieOption}>
                            <CheckCircle size={22} fill="#0f172a" color="white" />
                          </SelectMarker>
                        )}
                      </OptionCard>
                    )
                  })}
                </OptionsGrid>
              </div>
            </QuestionTwoCol>
            {currentQuestion.helpText && (
              <HelperRow>
                <div />
                <HelperText>
                  <div className="helper-icon">
                    <Lightbulb size={16} strokeWidth={2} />
                  </div>
                  <div className="helper-content">
                    <h4>{t('explanationTooltip')}</h4>
                    <p>{currentQuestion.helpText}</p>
                  </div>
                </HelperText>
              </HelperRow>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </QuestionContainer>
  );
};

export default QuestionStep;
